// Commission report PDF generation utility
// Generates a professional English-language PDF with monthly summaries and per-tour breakdowns
// Updated: Created for superadmin commission export feature

import jsPDF from 'jspdf';
import autoTable from 'jspdf-autotable';

export interface MonthReportData {
  monthKey: string;       // e.g. "2026-01"
  monthLabel: string;     // e.g. "January 2026"
  bookings: number;
  totalRevenue: number;
  totalCommission: number;
  tourBreakdown: {
    tourName: string;
    bookings: number;
    persons: number;
    revenue: number;
    commission: number;
    commissionType: 'percentage' | 'per_person' | null;
    commissionValue: number | null;
  }[];
}

export interface ReportSummary {
  totalBookings: number;
  totalRevenue: number;
  totalCommission: number;
  months: MonthReportData[];
}

const COMMISSION_RATE = 0.085;

export const calculateBookingCommission = (
  commissionType: 'percentage' | 'per_person' | null,
  commissionValue: number | null,
  totalPrice: number,
  persons: number
): number => {
  if (!commissionType || commissionValue == null) return 0;
  if (commissionType === 'percentage') {
    return commissionValue * totalPrice;
  }
  return COMMISSION_RATE * (commissionValue * persons);
};

const formatCommissionType = (
  commissionType: 'percentage' | 'per_person' | null,
  commissionValue: number | null,
  revenue: number,
  persons: number
): string => {
  if (!commissionType || commissionValue == null) return 'No commission';
  if (commissionType === 'percentage') {
    const pct = (commissionValue * 100).toFixed(1);
    return `${pct}% of ${formatCurrency(revenue)}`;
  }
  const perPerson = formatCurrency(commissionValue);
  const total = formatCurrency(commissionValue * persons);
  return `8.5% of ${perPerson} x ${persons} = ${total}`;
};

const formatCurrency = (amount: number): string => {
  return new Intl.NumberFormat('en-US', {
    style: 'currency',
    currency: 'USD',
    minimumFractionDigits: 2,
  }).format(amount);
};

const getMonthLabel = (monthKey: string): string => {
  const [year, month] = monthKey.split('-').map(Number);
  return new Date(year, month - 1, 1).toLocaleString('en-US', { month: 'long', year: 'numeric' });
};

export const generateCommissionPDF = (report: ReportSummary): void => {
  const doc = new jsPDF({ orientation: 'portrait', unit: 'mm', format: 'a4' });
  const pageWidth = doc.internal.pageSize.getWidth();
  const pageHeight = doc.internal.pageSize.getHeight();
  const margin = 14;

  // Header
  doc.setFillColor(31, 43, 31); // #1F2B1F
  doc.rect(0, 0, pageWidth, 30, 'F');

  doc.setTextColor(255, 255, 255);
  doc.setFontSize(18);
  doc.setFont('helvetica', 'bold');
  doc.text('Commission Report', margin, 13);

  doc.setFontSize(10);
  doc.setFont('helvetica', 'normal');
  doc.setTextColor(200, 200, 200);

  const sortedMonths = [...report.months].sort((a, b) => a.monthKey.localeCompare(b.monthKey));
  const firstMonth = sortedMonths[0]?.monthLabel || '';
  const lastMonth = sortedMonths[sortedMonths.length - 1]?.monthLabel || '';
  const dateRange = sortedMonths.length === 1
    ? firstMonth
    : `${firstMonth} - ${lastMonth}`;

  doc.text(`Period: ${dateRange}`, margin, 20);
  doc.text(`Generated: ${new Date().toLocaleString('en-US', { dateStyle: 'long', timeStyle: 'short' })}`, margin, 25);

  let cursorY = 38;

  // Grand summary table
  doc.setTextColor(31, 43, 31);
  doc.setFontSize(13);
  doc.setFont('helvetica', 'bold');
  doc.text('Summary', margin, cursorY);
  cursorY += 3;

  autoTable(doc, {
    startY: cursorY,
    head: [['Metric', 'Value']],
    body: [
      ['Total Bookings', String(report.totalBookings)],
      ['Total Revenue', formatCurrency(report.totalRevenue)],
      ['Total Commission', formatCurrency(report.totalCommission)],
      ['Average Commission / Booking', formatCurrency(report.totalBookings > 0 ? report.totalCommission / report.totalBookings : 0)],
    ],
    theme: 'striped',
    headStyles: { fillColor: [149, 59, 14], textColor: [255, 255, 255], fontSize: 10 },
    bodyStyles: { fontSize: 10, textColor: [31, 43, 31] },
    margin: { left: margin, right: margin },
  });

  cursorY = (doc as any).lastAutoTable.finalY + 10;

  // Monthly summary table
  doc.setFontSize(13);
  doc.setFont('helvetica', 'bold');
  doc.text('Monthly Overview', margin, cursorY);
  cursorY += 3;

  autoTable(doc, {
    startY: cursorY,
    head: [['Month', 'Bookings', 'Revenue', 'Commission']],
    body: sortedMonths.map((m) => [
      m.monthLabel,
      String(m.bookings),
      formatCurrency(m.totalRevenue),
      formatCurrency(m.totalCommission),
    ]),
    foot: [['Total', String(report.totalBookings), formatCurrency(report.totalRevenue), formatCurrency(report.totalCommission)]],
    theme: 'striped',
    headStyles: { fillColor: [31, 43, 31], textColor: [255, 255, 255], fontSize: 10 },
    footStyles: { fillColor: [149, 59, 14], textColor: [255, 255, 255], fontSize: 10, fontStyle: 'bold' },
    bodyStyles: { fontSize: 10, textColor: [31, 43, 31] },
    margin: { left: margin, right: margin },
  });

  // Per-tour breakdown for each month
  for (const monthData of sortedMonths) {
    cursorY = (doc as any).lastAutoTable.finalY + 12;

    // Check if we need a page break
    if (cursorY > pageHeight - 60) {
      doc.addPage();
      cursorY = 20;
    }

    doc.setFontSize(12);
    doc.setFont('helvetica', 'bold');
    doc.setTextColor(149, 59, 14);
    doc.text(`${monthData.monthLabel} - Tour Breakdown`, margin, cursorY);
    cursorY += 3;

    if (monthData.tourBreakdown.length === 0) {
      doc.setFontSize(10);
      doc.setFont('helvetica', 'italic');
      doc.setTextColor(120, 120, 120);
      doc.text('No bookings in this period.', margin, cursorY + 4);
      autoTable(doc, {
        startY: cursorY + 6,
        body: [[' ', ' ', ' ', ' ']],
        theme: 'plain',
        margin: { left: margin, right: margin },
      });
      continue;
    }

    autoTable(doc, {
      startY: cursorY,
      head: [['Tour', 'Bookings', 'Persons', 'Revenue', 'Commission Type', 'Commission']],
      body: monthData.tourBreakdown.map((t) => [
        t.tourName,
        String(t.bookings),
        String(t.persons),
        formatCurrency(t.revenue),
        formatCommissionType(t.commissionType, t.commissionValue, t.revenue, t.persons),
        formatCurrency(t.commission),
      ]),
      foot: [['Total', String(monthData.bookings), '', formatCurrency(monthData.totalRevenue), '', formatCurrency(monthData.totalCommission)]],
      theme: 'grid',
      headStyles: { fillColor: [42, 56, 42], textColor: [255, 255, 255], fontSize: 8 },
      footStyles: { fillColor: [31, 43, 31], textColor: [255, 255, 255], fontSize: 9, fontStyle: 'bold' },
      bodyStyles: { fontSize: 8, textColor: [31, 43, 31] },
      columnStyles: {
        0: { cellWidth: 44 },
        1: { cellWidth: 16, halign: 'center' },
        2: { cellWidth: 14, halign: 'center' },
        3: { cellWidth: 24, halign: 'right' },
        4: { cellWidth: 44 },
        5: { cellWidth: 24, halign: 'right' },
      },
      margin: { left: margin, right: margin },
    });
  }

  // Footer with page numbers
  const pageCount = doc.getNumberOfPages();
  for (let i = 1; i <= pageCount; i++) {
    doc.setPage(i);
    doc.setFontSize(8);
    doc.setTextColor(150, 150, 150);
    doc.setFont('helvetica', 'normal');
    doc.text(
      `Page ${i} of ${pageCount}`,
      pageWidth - margin,
      pageHeight - 8,
      { align: 'right' }
    );
    doc.text('Confidential - Commission Report', margin, pageHeight - 8);
  }

  // Build filename
  const monthKeys = sortedMonths.map((m) => m.monthKey);
  let filename: string;
  if (monthKeys.length === 1) {
    filename = `commission-report-${monthKeys[0]}.pdf`;
  } else {
    filename = `commission-report-${monthKeys[0]}-to-${monthKeys[monthKeys.length - 1]}.pdf`;
  }

  doc.save(filename);
};

export { getMonthLabel };
